import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { INTENT_VALUE_CLASS } from "@/shared/components/KpiCard";

export { KpiCard, type KpiCardTarget } from "@/shared/components/KpiCard";

interface WindowsProps {
  h1: number;
  h24: number;
  d7: number;
  /** Color a non-zero window — success for "good" counts (labels received),
   * danger for "bad" ones (stuck). Zero always stays neutral. */
  nonZeroIntent?: "success" | "danger";
}

const WINDOW_KEYS = ["h1", "h24", "d7"] as const;

/** Three labelled columns (1h / 24h / 7d) separated by dividers — the number
 * sits above its own window caption so the triple reads as three cells, not
 * one run of digits. */
export function Windows({ h1, h24, d7, nonZeroIntent }: WindowsProps): JSX.Element {
  const { t } = useTranslation("dashboard");
  const values = { h1, h24, d7 };
  const tone = nonZeroIntent ? INTENT_VALUE_CLASS[nonZeroIntent] : INTENT_VALUE_CLASS.neutral;

  return (
    <div className="flex items-stretch">
      {WINDOW_KEYS.map((key, i) => (
        <div
          key={key}
          className={`flex flex-col pr-4 ${i > 0 ? "border-l border-[var(--premium-border)] pl-4" : ""}`}
        >
          <span className={`tabular-nums ${values[key] > 0 ? tone : INTENT_VALUE_CLASS.neutral}`}>
            {values[key].toLocaleString()}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
            {t(`dashboard:win.${key}`)}
          </span>
        </div>
      ))}
    </div>
  );
}
